import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SerializeModuleService } from '../edit/serialize-module.service';
import { StoryModel } from '../story/models/story.model';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ModulesSaveResultEnum } from './models/modules-save-result.enum';
import { ScenePathModel } from './models/scene-path.model';
import { SaveModuleModel } from './models/save-module.model';
import { BookmarkModel } from '../bookmarks/models/bookmark.model';
import { ContentType } from '../story/models/content-type.enum';
import { ModulesMergeResultModel } from './models/modules-merge-result.model';
import { HumanInterface } from '../engine/models/human.interface';

declare const Human: HumanInterface;

const cdbURL = 'cdb/content';
const cdbCopyURL = `cdb/copy/content`;

const enum contentEnvironment {
  qa = 'qa',
  production = 'production',
}

const formHeaders = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json; charset=utf-8',
  }),
};

// top-level directories we wish to expose to content creators directly
const primaryCategories = [
  'production',
  'client',
  'preview',
  'internal_content',
  'public_preview',
  'demos',
  'social',
  'exports',
  'real_data',
];

export interface ListValue {
  name: string;
  type: string;
}

@Injectable({
  providedIn: 'root',
})
export class ModulesService {
  constructor(private http: HttpClient, private serializeModuleService: SerializeModuleService) {}

  // write the story module to the modules server
  saveModule(story: StoryModel, modulePath: string): Observable<ModulesSaveResultEnum> {
    modulePath = this.formatModulePath(modulePath);
    const url = [cdbURL, 'modules', modulePath].join('/');

    // !important: custom stringify, escape non-ASCII unicode sequences
    const storyJSONString = this.JSONStringify(story);

    return this.http.put(url, storyJSONString, formHeaders).pipe(
      // success
      map(() => ModulesSaveResultEnum.Success),
      // http status 201 | 202 treated as "error" by Angular HTTP, handle server response
      catchError(({ status }) => {
        if (status >= 200 && status <= 202) {
          return of(ModulesSaveResultEnum.Success);
        } else {
          return of(ModulesSaveResultEnum.Fail);
        }
      }),
    );
  }

  mergeModule(
    loftModuleId: string,
    bridgeModuleId: string,
    mergeOption: 'exception' | 'bridge' | 'loft' = 'exception',
  ): Observable<ModulesMergeResultModel> {
    const params = {
      loft: `${loftModuleId}.json`,
      bridge: `${bridgeModuleId}.json`,
      merge_option: mergeOption,
    };

    return this.http.post<ModulesMergeResultModel>('/cdb/merge/modules', params);
  }

  // Post a request to make a hard copy of content state directory
  copyState(sourceId: string, targetId: string): Observable<boolean> {
    const url = [cdbCopyURL, `states`, targetId].join('/');
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        'source-state': sourceId,
      }),
    };
    return this.http.put<boolean>(url, {}, options);
  }

  listContents(
    primaryCategory: string,
    secondaryCategory: string,
    name: string,
    convertToBookmark = false,
  ): Observable<ListValue[] | BookmarkModel[]> {
    const path = this.generateModulePath(primaryCategory, secondaryCategory || '', name || '');

    return this.http.get(`/cdb/ls/content/modules/${path}`).pipe(
      map((results: { contents: ListValue[] }) => {
        if (convertToBookmark) {
          const _results: BookmarkModel[] = results.contents.map((value) => {
            // For loft content, ID and Label both just use filename.
            // For label, strip off the leading directories for easier parsing.
            const id = value.name.replace('/content/modules/', '').replace('.json', '');

            const title = value.name
              .split('/')
              .pop()
              .replace('/content/modules', '')
              .replace('.json', '');

            const bookmark = new BookmarkModel();
            bookmark.id = id;
            bookmark.title = title;
            bookmark.type = ContentType.Module;
            return bookmark;
          });

          return _results;
        } else {
          return results.contents as ListValue[];
        }
      }),
    );
  }

  getState(id: string) {
    return this.http.get(`/cdb/ls/content/states/${id}`).pipe(
      map((results: any) => {
        return results.contents as Array<ListValue>;
      }),
    );
  }

  // return list of BioDigital Client directories
  getClients(): Observable<string[]> {
    return this.http.get(`/search/client`).pipe(
      map((response: { clients: string[] }) => {
        return response.clients;
      }),
    );
  }

  // Iterates through an entire scene and determine what states are in use by all the assets.
  // A very thorough check resulting in quite a limited output.
  getUsedStates() {
    const states = {};

    Object.values(Human.scene.objects).reduce((result, object: any) => {
      ['transformId', 'morphId', 'geometryId', 'materialId'].forEach((property) => {
        const state = this.getPropertyState(object, property);
        if (state.length) {
          states[state] = true;
        }
      });

      if (object.hasOwnProperty('renderables')) {
        ['morphId', 'geometryId', 'materialId'].forEach((property) => {
          const renderables = object.renderables as Array<any>;
          renderables.forEach((renderable) => {
            const state = this.getPropertyState(renderable, property);
            if (state.length) {
              states[state] = true;
            }
          });
        });
      }

      return result;
    }, {});

    return Object.keys(states);
  }

  // Get the semantically meaningful parts of a module path
  parseScenePath(id: string): ScenePathModel {
    const parts = id.split('/');
    if (parts.length === 1) {
      // invalid scene path
      return null;
    }

    // # NOTE: [EM] hardcoded content environment
    const urlSubDomain = contentEnvironment.qa;
    return {
      fullPath: `https://${urlSubDomain}.biodigital.com/content/modules/${id}`,
      relativePath: parts.slice(0, parts.length - 1).join('/'),
      sceneName: parts[parts.length - 1],
    };
  }

  // ensure the moduleId has a .json extension
  private formatModulePath(modulePath: string) {
    let result = modulePath;
    if (!/\.json$/.test(modulePath)) {
      result = `${result}.json`;
    }
    return result;
  }

  // Return a list of primary modules categories
  getPrimaryCategories(): Observable<string[]> {
    return of(primaryCategories);
  }

  // Return a list of secondary catogories, given a primary category
  getSecondaryCategories(primaryCategory: string): Observable<string[]> {
    switch (primaryCategory) {
      case 'production': {
        // list gender categories
        return of(['maleAdult', 'femaleAdult']);
      }
      case 'client': {
        // list client-projects
        return this.getClients();
      }
      default: {
        // no secondary category available
        return of([]);
      }
    }
  }

  generateSaveModule(id: string): SaveModuleModel {
    const scenePath = this.parseScenePath(id);
    const categories = scenePath.relativePath.split('/');

    return {
      id: id,
      name: scenePath.sceneName,
      primaryCategory: categories[0] || '',
      secondaryCategory: categories[1] || '',
    };
  }

  generateModulePath(primaryCategory: string, secondaryCategory: string, name: string) {
    const path = [];
    if (primaryCategory.length) {
      path.push(primaryCategory);
    }

    if (secondaryCategory.length) {
      path.push(secondaryCategory);
    }

    if (name.length) {
      if (name.indexOf('.json') < 0) {
        name = `${name}.json`;
      }
      path.push(name);
    }

    return path.join('/');
  }

  private getPropertyState(object, property) {
    if (object[property] != null) {
      return object[property].split('/')[0];
    }

    return '';
  }

  // #TODO: centralize/reuse
  private JSONStringify(object: any): string {
    return JSON.stringify(object).replace(/[\u007f-\uffff]/g, (character) => {
      return '\\u' + ('0000' + character.charCodeAt(0).toString(16)).slice(-4);
    });
  }
}
